import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { VIEW_TRIP_KEY } from "../lib/api";
import { clearCache } from "../lib/swr";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // null = loading
  const [org, setOrg] = useState(null);
  const [config, setConfig] = useState(null);
  const [trip, setTrip] = useState([]);
  const [ready, setReady] = useState(false);

  const applyAccent = useCallback((accent) => {
    if (accent) document.documentElement.style.setProperty("--tenant-accent", accent);
  }, []);

  const refreshTrip = useCallback(async () => {
    try { const { data } = await api.get("/itinerary/countries"); setTrip(data); }
    catch (e) { console.debug("refreshTrip failed", e?.message); }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data.token) localStorage.setItem("mc_token", data.token);
      setUser(data.user);
      setOrg(data.organization);
      setConfig(data.config);
      // Viewing a trip that no longer exists (or was deleted) → fall back to the current one.
      const wanted = localStorage.getItem(VIEW_TRIP_KEY);
      if (wanted && data.config?.id && data.config.id !== wanted) localStorage.removeItem(VIEW_TRIP_KEY);
      if (data.organization?.slug) localStorage.setItem("mc_org_slug", data.organization.slug);
      applyAccent(data.organization?.accent_color);
      refreshTrip();
      return data.user;
    } catch {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, [applyAccent, refreshTrip]);

  // Switch which journey the whole app is looking at. Full reload keeps every page's data coherent.
  const setViewTrip = (tripId, currentTripId) => {
    clearCache();
    if (!tripId || tripId === currentTripId) localStorage.removeItem(VIEW_TRIP_KEY);
    else localStorage.setItem(VIEW_TRIP_KEY, tripId);
    window.location.assign("/app");
  };
  const isLookingBack = !!config?.id && !!org?.current_trip_id && config.id !== org.current_trip_id;
  const isReadOnly = isLookingBack && config?.status === "past";

  useEffect(() => {
    if (localStorage.getItem("mc_token")) loadMe();
    else { setUser(false); setReady(true); }
  }, [loadMe]);

  const afterAuth = (data) => {
    localStorage.setItem("mc_token", data.token);
    return loadMe();
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return afterAuth(data);
  };
  const registerOrg = async (payload) => {
    const { data } = await api.post("/auth/register-org", payload);
    return afterAuth(data);
  };
  const registerPartner = async (payload) => {
    const { data } = await api.post("/auth/register-partner", payload);
    return afterAuth(data);
  };
  const verifyCode = async (email, code) => {
    const { data } = await api.post("/auth/verify-code", { email, code });
    return afterAuth(data);
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("logout request failed", e?.message); }
    clearCache();
    localStorage.removeItem("mc_token");
    localStorage.removeItem(VIEW_TRIP_KEY);
    setUser(false); setOrg(null); setConfig(null); setTrip([]);
  };
  const refreshOrg = async () => {
    const { data } = await api.get("/org");
    setOrg(data); applyAccent(data?.accent_color);
  };
  const refreshConfig = async () => {
    const { data } = await api.get("/config");
    setConfig(data);
  };

  const can = useCallback((perm) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.role === "team") return !!(user.permissions || {})[perm];
    return false;
  }, [user]);
  const isTeam = !!user && (user.role === "admin" || user.role === "team");
  const isOwner = !!user && user.role === "admin" && !user.co_admin;

  const canViewFinancials = useCallback(() => {
    if (!user) return false;
    const level = org?.financial_visibility || "partners";
    if (user.role === "admin") return true;
    if (user.role === "team") return level === "hidden" ? !!(user.permissions || {}).finances : true;
    if (user.role === "partner") {
      if (level === "public" || level === "partners") return true;
      if (level === "financial_partners") return (user.partnership_ways || []).includes("financial");
      return false;
    }
    return false;
  }, [user, org]);
  // A prayer-only partner blocked specifically because they're not a financial partner → upsell to convert.
  const financialsUpsell = useCallback(() => {
    const level = org?.financial_visibility || "partners";
    return user?.role === "partner" && level === "financial_partners" && !(user.partnership_ways || []).includes("financial");
  }, [user, org]);

  return (
    <AuthContext.Provider value={{ user, org, config, trip, ready, login, registerOrg, registerPartner, verifyCode, logout, refreshOrg, refreshConfig, refreshTrip, refreshMe: loadMe, applyAccent, can, isTeam, isOwner, canViewFinancials, financialsUpsell, setViewTrip, isLookingBack, isReadOnly }}>
      {children}
    </AuthContext.Provider>
  );
}
